import React, { useContext } from "react";

import Button from "@atlaskit/button-next/new";
import DropdownMenu, {
    DropdownItemGroup,
    CustomTriggerProps
} from "@atlaskit/dropdown-menu";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import { Box, xcss } from "@atlaskit/primitives-next";

import { FastshiftTableFilterContext } from "../fastshift-table-filter-context";

import { FastshiftTableFilterDropdownItem } from "./fastshift-table-filter-dropdown-item";
import { Option } from "./types";

interface FastshiftTableFilterDropdownProps {
    filterKeyId: string;
    label: React.ReactNode;
    options: Option[];
    customOptionContent?: (option: Option) => React.ReactNode;
    onOptionClick?: (option: Option) => void;
}

interface DropdownTriggerProps<T extends HTMLElement>
    extends CustomTriggerProps<T> {
    label: React.ReactNode;
}

const labelStyle = xcss({
    marginInlineEnd: "space.100",
    display: "inline-block"
});

const DropdownTrigger = ({
    label,
    onClick,
    isSelected,
    triggerRef,
    ...rest
}: DropdownTriggerProps<HTMLButtonElement>) => {
    return (
        <Button
            {...rest}
            ref={triggerRef}
            onClick={onClick}
            isSelected={isSelected}
        >
            <Box xcss={labelStyle}>{label}</Box>
            <ChevronDownIcon label="" size="small" />
        </Button>
    );
};

export const FastshiftTableFilterDropdown: React.FC<
    FastshiftTableFilterDropdownProps
> = ({ filterKeyId, label, options, customOptionContent, onOptionClick }) => {
    const { filters, setFilter } = useContext(FastshiftTableFilterContext);

    const selectedOption = filters[filterKeyId];

    return (
        <DropdownMenu
            trigger={(props: CustomTriggerProps<HTMLButtonElement>) =>
                DropdownTrigger({
                    ...props,
                    label: selectedOption
                        ? customOptionContent
                            ? customOptionContent(selectedOption)
                            : selectedOption.label
                        : label,
                    isSelected: !!selectedOption
                })
            }
            spacing="compact"
            shouldRenderToParent
        >
            <DropdownItemGroup>
                {options.map((option) => (
                    <FastshiftTableFilterDropdownItem
                        key={option.value}
                        option={option}
                        onClick={() => {
                            setFilter(filterKeyId, option);
                            onOptionClick?.(option);
                        }}
                        isSelected={selectedOption?.value === option.value}
                        customContent={
                            customOptionContent
                                ? customOptionContent(option)
                                : undefined
                        }
                    />
                ))}
            </DropdownItemGroup>
        </DropdownMenu>
    );
};
